import { useEffect, useRef, useState } from 'react';
import { types, viewOptions } from '../constants/savedAgents';
import {
    getDefaultContactInfoColumns,
    getDefaultOverviewColumns,
    getMlsRestrictionSettings,
    getRowDataSortedAndRanked,
    getDefaultActivityColumns
} from '../helpers/agentProductionHelpers';
import { useCommonStore, useSavedAgentsStore, useSearchStore } from '../../store/store';
import { getDefaultColumns as getDefaultListingProfColumns } from '../helpers/listingProficiencyHelpers';
import { growthAnalysisColumnsByPeriod } from '../components/helpers/growthAnalysisHelpers';
import { removeRowByKey } from '../../common/helpers/agGrid';
import { setStateData } from '../../common/helpers/state';
import { getKeyByValue } from '../../common/helpers/object';
import { TimePeriods, agentProductionTerms as terms, modules } from '../../constants';
import { mapActivitiesRowData } from '../components/savedAgents/helpers/dataMappers';
import { views } from '../constants/savedAgents';
import { useSearchResultsStore } from '../../store/searchResults/store';
import { getSelectedAgentList } from '../components/savedAgents/helpers';
import { useAuthStore } from '../../store/auth/store';
import { getOwnerIdByTokenInfo } from '../../constants/auth';
import { getCriteriaObject } from '../../common/helpers/utilities';

export const useSavedAgents = ({ setModuleAgentData, isActivityRoute }) => {
    const savedAgentsModule = modules.proficiencyMetrics.savedAgents;
    const gridRef = useRef();

    const {
        currentModule,
        isManualSearch,
        clearSavedAgentsOptionalFilterTitles,
        clearSavedAgentsSearch,
        clearSelectedAgentIds,
        selectedViewHideAgentsFilter,
        selectedSavedAgentListId,
    } = useSearchStore((state) => ({
        currentModule: state.selectedModule,
        isManualSearch: state.isManualSearch,
        clearSavedAgentsOptionalFilterTitles: state.clearSavedAgentsOptionalFilterTitles,
        clearSavedAgentsSearch: state.clearSavedAgentsSearch,
        clearSelectedAgentIds: state.clearSelectedAgentIds,
        selectedViewHideAgentsFilter: state[state.selectedModule]?.selectedViewHideAgentsFilter,
        selectedSavedAgentListId:
            getCriteriaObject(state[modules.proficiencyMetrics.savedAgents]?.search?.searchCriteria)
                ?.idFiltering[0]?.listId,
    }));    

    const {
        currentAgentList,
        timeIntervals,
        agentLists,
        getAgentLists,
        popoverDataForAddAgentList,
        toggleAgentListsPopover,
        lastUpdatedAgentList,
        privileges
    } = useCommonStore((state) => ({
        currentAgentList: state?.agentLists?.find(l => l.agentListId === selectedSavedAgentListId)?.agentIds,
        timeIntervals: state.timeIntervals,
        agentLists: state.agentLists,
        getAgentLists: state.getAgentLists,
        popoverDataForAddAgentList: state.popoverDataForAddAgentList,
        toggleAgentListsPopover: state.toggleAgentListsPopover,
        lastUpdatedAgentList: state.lastUpdatedAgentList,
        privileges: state.privileges
    }));

    const {
        savedAgentsSelectedList,
        globalSavedAgentListId,
        runSelectedList,
        setSelectedListByModule,
        selectedView,
        manualSearchData,
        activities,
        dirtyListIds,
        setSelectedView
    } = useSavedAgentsStore((state) => ({
        savedAgentsSelectedList: state.selectedListByModule[savedAgentsModule],
        globalSavedAgentListId: state.globalSavedAgentListId,
        runSelectedList: state.runSelectedList,
        setSelectedListByModule: state.setSelectedListByModule,
        selectedView: state.selectedView,
        manualSearchData: state.manualSearchData,
        activities: state.activities,
        dirtyListIds: state.dirtyListIds,
        setSelectedView: state.setSelectedView
    }));

    const {
        tokenInfo,
        tokenType,
    } = useAuthStore(state => ({
        tokenInfo: state.tokenInfo,
        tokenType: state.tokenType,
    }));
    
    const restrictedMlsSettings = getMlsRestrictionSettings(savedAgentsSelectedList?.mlsId);

    const { searchResults, setSavedAgentsSearchResults, resetSavedAgentsSearchResults } = useSearchResultsStore(
        (state) => ({
            searchResults: state[currentModule]?.searchResults,
            setSavedAgentsSearchResults: state.setSavedAgentsSearchResults,
            resetSavedAgentsSearchResults: state.resetSavedAgentsSearchResults
        })
    );

    const [state, setState] = useState({
        rowData: [],
        activityRowData: [],
        overviewColumns: getDefaultOverviewColumns(),
        contactInfoColumns: getDefaultContactInfoColumns(restrictedMlsSettings),
        listingProfColumns: getDefaultListingProfColumns(),
        activityColumns: getDefaultActivityColumns(),
        currentSearchDates: {
            lastIntervalText: '',
            from: null,
            to: null
        },
        growthAnalysisColumns: growthAnalysisColumnsByPeriod[terms.last12Months],
        growthAnalysisPeriod: terms.last12Months,
        timeIntervals,
        searchCriteria: undefined,
        showModal: false,
        modalProps: {
            dataLwtId: 'agent-production-modal',
            show: false,
            size: '',
            title: '',
            footerClassName: '',
            showFooter: false,
            onClose: () => null,
            children: null
        },
        prevSelectedList: savedAgentsSelectedList,
        agentData: []
    });

    const removeAgentInSearchStoreData = (agentId) => {
        const updatedAgentData = state.agentData?.filter((agent) => agent.agentId !== agentId);
        setStateData('agentData', updatedAgentData, setState);
    };

    const removeAgentFromTableAndModuleSearch = (gridRef, agentId) => {
        if (!gridRef.current || !gridRef.current?.api) return;
        removeRowByKey(gridRef, 'agentId', agentId);
        toggleAgentListsPopover();
        removeAgentInSearchStoreData(agentId);
    };
    
    const manualSearchDataRef = useRef(manualSearchData);
    useEffect(() => { manualSearchDataRef.current = manualSearchData; }, [manualSearchData]);

    const lastRunListIdRef = useRef();

    useEffect(() => {
        const ownerId = tokenType && getOwnerIdByTokenInfo[tokenType](tokenInfo);        
        if (agentLists?.length && !selectedSavedAgentListId) return;   
        getAgentLists(ownerId);
    }, [tokenInfo, tokenType, selectedSavedAgentListId]);

    useEffect(() => {
        if (isActivityRoute) {
            setSelectedView(viewOptions[0]);
            useSavedAgentsStore.setState({ activities: null });
        }
    }, [isActivityRoute]);

    useEffect(() => {
        // Important: Use manual search data if available, otherwise use local agentData
        const dataToUse = isManualSearch ? searchResults : state.agentData;
        if (!dataToUse?.length || dataToUse.error) return;
        const sortedData = getRowDataSortedAndRanked(dataToUse);
        setStateData('rowData', sortedData, setState);
        if (JSON.stringify(searchResults) !== JSON.stringify(sortedData)) {
            setSavedAgentsSearchResults(sortedData);
        }
    }, [state.agentData, isManualSearch]);

    useEffect(() => {
        const shouldClearData =
            selectedView?.value !== views.activity ||
            !activities?.length ||
            !state.agentData?.length;

        if (shouldClearData) {
            setStateData('activityRowData', [], setState);
            return;
        }
        const enrichedRowData = mapActivitiesRowData(state.rowData, activities);
        setStateData('activityRowData', enrichedRowData, setState);
    }, [activities, selectedView?.value, state.rowData]);

    useEffect(() => {
        // Important:Clear computedFields before running search when new saved agent list is chosen.
        const currentListId = savedAgentsSelectedList?.agentListId;
        const ALL_LISTS_PATTERN = /^all-lists(-\d+)?$/;
        const isAllLists = ALL_LISTS_PATTERN.test(currentListId?.toString());
        const hasListChanged = currentListId !== state.prevSelectedList?.agentListId ||
            (!isAllLists && currentAgentList?.length !== manualSearchDataRef.current?.map(l => l.agentId)?.length);
        const isListDirty = currentListId && dirtyListIds[currentListId];
        const shouldRefetch = hasListChanged || isListDirty;
        if (lastRunListIdRef.current === currentListId && !isListDirty) return;
        if (shouldRefetch) {
            useSearchStore.getState().resetSearchCriteria();
            clearSavedAgentsSearch();
            clearSavedAgentsOptionalFilterTitles();
            resetSavedAgentsSearchResults();
            clearSelectedAgentIds();
            setStateData('activityRowData', [], setState);
        }
        lastRunListIdRef.current = currentListId;
        runSelectedList(setState, setModuleAgentData, shouldRefetch);
    }, [
        runSelectedList,
        savedAgentsSelectedList?.agentListId,
        state.prevSelectedList,
        dirtyListIds
    ]);

    useEffect(() => {
        setStateData(
            'growthAnalysisColumns',
            growthAnalysisColumnsByPeriod[state.growthAnalysisPeriod],
            setState
        );
    }, [state.growthAnalysisPeriod]);

    useEffect(() => {
        if (!timeIntervals?.length) return;
        let currentSearchDates;
        const interval = timeIntervals?.find((i) => i?.intervalType === terms.annually);
        if (interval) {
            const lastIntervalText = getKeyByValue(TimePeriods, terms.annually);
            const from = new Date(`${interval.last.from}Z`);
            const to = new Date(`${interval.last.to}Z`);
            currentSearchDates = { lastIntervalText, from, to };
        }
        setStateData('currentSearchDates', currentSearchDates, setState);
    }, [timeIntervals]);

    useEffect(() => {
        if (currentModule === savedAgentsModule) {
            const { agentId } = popoverDataForAddAgentList;
            const isAgentInList = savedAgentsSelectedList?.agentIds?.includes(agentId);
            const lastUpdatedAgentListMatch = lastUpdatedAgentList === savedAgentsSelectedList?.agentListId;
            lastUpdatedAgentListMatch &&
            isAgentInList &&
            removeAgentFromTableAndModuleSearch(gridRef, agentId);
        }
        if (!agentLists?.length) return;
        const selectedAgentListId = savedAgentsSelectedList?.agentListId || globalSavedAgentListId;
        setSelectedListByModule(savedAgentsModule, getSelectedAgentList(agentLists, selectedAgentListId));
    }, [agentLists, savedAgentsSelectedList?.agentListId, globalSavedAgentListId]);

    useEffect(() => {
        if (!savedAgentsSelectedList || !state.agentData || state.agentData.error) return;
        const { userOffices = [] } = privileges || {};
        const userMlsOffices = userOffices.filter(
            (office) => office.mlsId === savedAgentsSelectedList.mlsId
        );
        const userFilteredOffices = userMlsOffices.map((office) => office.officeIds)[0];
        const agentResults = getRowDataSortedAndRanked(state.agentData);
        const filteredAgents = agentResults.filter((agent) => {
            if (selectedViewHideAgentsFilter === types.ONLY_SHOW_MY_AGENTS) {
                return userFilteredOffices.includes(agent.officeId);
            }
            if (selectedViewHideAgentsFilter === types.HIDE_MY_AGENTS) {
                return !userFilteredOffices.includes(agent.officeId);
            }
            return true;
        });
        const shouldNotRank = true;
        setStateData('rowData', getRowDataSortedAndRanked(filteredAgents, shouldNotRank), setState);
    }, [selectedViewHideAgentsFilter, privileges, savedAgentsSelectedList, state.agentData]);

    return {
        state: { ...state, selectedView },
        setState,
        gridRef
    };
};
